/**
 * Onboarding View — Step-by-step wizard
 * Collects essential info one question per page (6 steps).
 * After completion, dumps users into /matches.
 */

import { render, showToast, showApiError, loadUserData } from '../main';
import { profileApi, preferencesApi, gendersApi, relationshipStatusesApi } from '../api';
import { store } from '../state';
import { navigate } from '../router';
import { loadingSpinner } from '../utils';
import { calculateAge } from '../utils/format';
import { isPnta } from '../options';
import { MIN_AGE } from '../config';
import { loadPhotos } from './profile/photo-manager';
import { renderPntaCheckbox, renderVisiCheckbox } from './profile/form-renderers';
import {
  BTN_SAVE_NEXT,
  TOAST_PROFILE_CREATED,
  TOAST_ENTER_NAME,
  TOAST_ENTER_BIRTHDAY,
  TOAST_SELECT_ANSWER,
  toastMinAge,
  API_ERR_SAVE_PROFILE,
} from '../utils/app-strings';

const TOTAL_STEPS = 6;
let currentStep = 1;
let genders: { id: number; name: string }[] = [];
let relationshipStatuses: { id: number; name: string }[] = [];

function isPrivateName(name: string | null | undefined): boolean {
  return /^Private-\d{5}$/.test(name || '');
}

/** Read current visi_json from store (defaults to empty object). */
function getVisiJson(): Record<string, any> {
  return store.getState().user?.preferences?.visi_json || {};
}

export async function renderOnboarding(): Promise<void> {
  render(`
    <div class="view-frame">
      <div class="onboarding-progress">
        <div class="onboarding-progress-fill" id="onboarding-progress-fill"></div>
      </div>
      <div class="view-scroll" id="onboarding-container">
        ${loadingSpinner()}
      </div>
    </div>
  `, false);

  await loadFormData();
}

async function loadFormData(): Promise<void> {
  const container = document.getElementById('onboarding-container')!;

  try {
    genders = store.getState().genders;
    relationshipStatuses = store.getState().relationshipStatuses;

    if (genders.length === 0 || relationshipStatuses.length === 0) {
      const [gendersRes, rsRes] = await Promise.all([
        gendersApi.getAll(),
        relationshipStatusesApi.getAll(),
      ]);
      genders = gendersRes.data;
      relationshipStatuses = rsRes.data;
      store.setGenders(genders);
      store.setRelationshipStatuses(relationshipStatuses);
    }

    currentStep = 1;
    renderStep();
  } catch (err: any) {
    console.error('Failed to load onboarding data:', err);
    container.innerHTML = `
      <div class="empty-state">
        <i class="fas fa-exclamation-circle"></i>
        <h3>Something went wrong</h3>
        <p>Unable to load setup form</p>
        <button class="btn btn-primary" id="retry-onboarding-btn">Retry</button>
      </div>
    `;
    document.getElementById('retry-onboarding-btn')?.addEventListener('click', loadFormData);
  }
}

function updateProgress(): void {
  const fill = document.getElementById('onboarding-progress-fill');
  if (fill) {
    fill.style.width = `${(currentStep / TOTAL_STEPS) * 100}%`;
  }
}

async function renderStep(): Promise<void> {
  updateProgress();

  const container = document.getElementById('onboarding-container');
  if (!container) return;

  switch (currentStep) {
    case 1: renderBirthdayStep(container); break;
    case 2: await renderNameStep(container); break;
    case 3: renderGenderStep(container); break;
    case 4: renderRelationshipStep(container); break;
    case 5: renderLocationStep(container); break;
    case 6: renderPhotoStep(container); break;
  }

  // Scroll to top on each step
  container.scrollTop = 0;
}

function stepLabel(): string {
  return `<p class="onboarding-step-label">Step ${currentStep} of ${TOTAL_STEPS}</p>`;
}

function backButton(): string {
  if (currentStep <= 1) return '';
  return `<button type="button" class="btn btn-secondary" id="onboarding-back-btn">Back</button>`;
}

function attachBackHandler(): void {
  document.getElementById('onboarding-back-btn')?.addEventListener('click', () => {
    currentStep--;
    renderStep();
  });
}

/** Wire up a PNTA checkbox to disable/enable all radios in the associated group. */
function setupPntaForGroup(groupId: string): void {
  const pnta = document.getElementById(`${groupId}_pnta`) as HTMLInputElement | null;
  if (!pnta) return;

  const toggle = () => {
    const radios = document.querySelectorAll<HTMLInputElement>(`input[name="${groupId}"]`);
    radios.forEach(r => {
      r.disabled = pnta.checked;
      if (pnta.checked) r.checked = false;
    });
  };

  pnta.addEventListener('change', toggle);
  // Apply initial state
  if (pnta.checked) toggle();
}

/** Read all checked visi checkboxes and save them. */
async function saveVisiFromDOM(): Promise<void> {
  const visiCheckboxes = document.querySelectorAll<HTMLInputElement>('[data-visi-field]');
  if (visiCheckboxes.length === 0) return;

  const patch: Record<string, boolean> = {};
  visiCheckboxes.forEach(cb => {
    // visi checkbox checked = "do NOT display" → visi value false
    patch[cb.dataset.visiField!] = !cb.checked;
  });

  try {
    await preferencesApi.updateVisibility(patch);
  } catch {
    // Non-critical — don't block the flow
  }
}

// ─── Step 1: Birthday ───────────────────────────────────────────────────────

function renderBirthdayStep(container: HTMLElement): void {
  const profile = store.getState().user?.profile;
  const existingDate = profile?.birth_date?.split('T')[0] || '2000-01-01';

  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">When is your birthday?</h2>

      <div class="input-group">
        <span class="group-label">Birthday <span class="label-hint" id="age-display"></span></span>
        <input
          type="date"
          id="birth_date"
          class="input"
          value="${existingDate}"
        />
        <small class="field-note">* Birthday is required to confirm you are 18 or older.<br><br>Your exact birthdate is never shared. If you prefer, select Do not display on profile to hide your age in years.</small>
        ${renderVisiCheckbox('show_age', getVisiJson())}
      </div>

      <div class="onboarding-actions">
        ${backButton()}
        <button class="btn btn-primary" id="onboarding-save-btn">${BTN_SAVE_NEXT}</button>
      </div>
    </div>
  `;

  const birthInput = document.getElementById('birth_date') as HTMLInputElement;
  const ageDisplay = document.getElementById('age-display')!;
  const saveBtn = document.getElementById('onboarding-save-btn') as HTMLButtonElement;

  const updateAgeDisplay = () => {
    const age = calculateAge(birthInput.value);
    if (age !== null && age >= MIN_AGE && age <= 120) {
      ageDisplay.textContent = `${age.toFixed(1)} years old`;
      ageDisplay.classList.remove('age-display-error');
      saveBtn.disabled = false;
    } else if (age !== null && age < MIN_AGE) {
      ageDisplay.textContent = `Must be at least ${MIN_AGE} years old`;
      ageDisplay.classList.add('age-display-error');
      saveBtn.disabled = true;
    } else {
      ageDisplay.textContent = '';
      saveBtn.disabled = !birthInput.value;
    }
  };

  birthInput.addEventListener('input', updateAgeDisplay);
  updateAgeDisplay();

  saveBtn.addEventListener('click', async () => {
    const birthDate = birthInput.value;
    if (!birthDate) {
      showToast(TOAST_ENTER_BIRTHDAY, 'error');
      return;
    }
    const age = calculateAge(birthDate);
    if (age !== null && age < MIN_AGE) {
      showToast(toastMinAge(MIN_AGE), 'error');
      return;
    }
    await saveVisiFromDOM();
    await saveAndAdvance({ birth_date: birthDate }, saveBtn);
  });

  attachBackHandler();
}

// ─── Step 2: Name ───────────────────────────────────────────────────────────

/** Extract a suggested name from the user's email (part before @), capitalized. */
function nameFromEmail(): string {
  const email = store.getState().user?.email;
  if (!email) return '';
  const local = email.split('@')[0] || '';
  // Strip digits and special chars, take first segment
  const cleaned = local.replace(/[^a-zA-Z]/g, ' ').trim().split(/\s+/)[0] || '';
  if (cleaned.length < 2) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

async function renderNameStep(container: HTMLElement): Promise<void> {
  const profile = store.getState().user?.profile;
  const existingName = profile?.first_name || '';
  const nameIsPrivate = isPrivateName(existingName);

  // If no name set yet, suggest one from email with server-side collision check
  let suggestedName = existingName;
  if (!existingName && !nameIsPrivate) {
    const base = nameFromEmail();
    if (base) {
      try {
        const res = await profileApi.checkName(base);
        suggestedName = res.data.suggestion || '';
      } catch {
        suggestedName = base; // API error — just use the base
      }
    }
  }

  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">What should we call you?</h2>

      <div class="input-group">
        <label for="user_display_str" class="group-label">First Name or Username</label>
        <input
          type="text"
          id="user_display_str"
          class="input"
          autocomplete="off"
          data-lpignore="true"
          data-1p-ignore
          value="${nameIsPrivate ? existingName : suggestedName}"
          placeholder="Your first name"
          ${nameIsPrivate ? 'disabled' : ''}
        />
        <label class="pnta-checkbox">
          <input type="checkbox" id="user_display_str_pnta" ${nameIsPrivate ? 'checked' : ''} />
          Prefer not to answer
        </label>
      </div>

      <div class="onboarding-actions">
        ${backButton()}
        <button class="btn btn-primary" id="onboarding-save-btn">${BTN_SAVE_NEXT}</button>
      </div>
    </div>
  `;

  const nameInput = document.getElementById('user_display_str') as HTMLInputElement;
  const pntaCheckbox = document.getElementById('user_display_str_pnta') as HTMLInputElement;
  const saveBtn = document.getElementById('onboarding-save-btn') as HTMLButtonElement;

  pntaCheckbox.addEventListener('change', () => {
    nameInput.disabled = pntaCheckbox.checked;
    if (pntaCheckbox.checked) {
      nameInput.value = '';
    }
  });

  saveBtn.addEventListener('click', async () => {
    if (!pntaCheckbox.checked && !nameInput.value.trim()) {
      showToast(TOAST_ENTER_NAME, 'error');
      return;
    }
    const payload: Record<string, any> = {};
    if (pntaCheckbox.checked) {
      payload.first_name = 'pnta';
    } else {
      payload.first_name = nameInput.value.trim();
    }
    await saveAndAdvance(payload, saveBtn);
  });

  attachBackHandler();
}

// ─── Step 3: Gender ─────────────────────────────────────────────────────────

function renderGenderStep(container: HTMLElement): void {
  const profile = store.getState().user?.profile;

  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">What is your gender?</h2>

      <div class="input-group">
        <span class="group-label">My Gender <span class="label-hint">I am a...</span></span>
        <div class="radio-group" id="gender_id">
          ${genders.filter(g => g.name !== 'Prefer not to say').map(g => `
            <label class="radio-item">
              <input
                type="radio"
                name="gender_id"
                value="${g.id}"
                ${profile?.gender_id === g.id ? 'checked' : ''}
              />
              <span class="radio-circle"></span>
              ${g.name}
            </label>
          `).join('')}
        </div>
        ${renderPntaCheckbox('gender_id', false)}
        ${renderVisiCheckbox('show_gender', getVisiJson())}
      </div>

      <div class="onboarding-actions">
        ${backButton()}
        <button class="btn btn-primary" id="onboarding-save-btn">${BTN_SAVE_NEXT}</button>
      </div>
    </div>
  `;

  setupPntaForGroup('gender_id');

  const saveBtn = document.getElementById('onboarding-save-btn') as HTMLButtonElement;
  const pnta = document.getElementById('gender_id_pnta') as HTMLInputElement;

  saveBtn.addEventListener('click', async () => {
    const selected = document.querySelector('input[name="gender_id"]:checked') as HTMLInputElement | null;
    if (!pnta.checked && !selected) {
      showToast(TOAST_SELECT_ANSWER, 'error');
      return;
    }
    await saveVisiFromDOM();
    // If PNTA, just advance without saving gender_id
    if (pnta.checked) {
      currentStep++;
      renderStep();
      return;
    }
    await saveAndAdvance({ gender_id: parseInt(selected!.value) }, saveBtn);
  });

  attachBackHandler();
}

// ─── Step 4: Relationship Status ────────────────────────────────────────────

function renderRelationshipStep(container: HTMLElement): void {
  const profile = store.getState().user?.profile;

  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">What is your relationship status?</h2>

      <div class="input-group">
        <span class="group-label">My Relationship Status</span>
        <div class="radio-group" id="relationship_status_id">
          ${relationshipStatuses.filter(rs => rs.name !== 'Prefer not to say').map(rs => `
            <label class="radio-item">
              <input
                type="radio"
                name="relationship_status_id"
                value="${rs.id}"
                ${profile?.relationship_status_id === rs.id ? 'checked' : ''}
              />
              <span class="radio-circle"></span>
              ${rs.name}
            </label>
          `).join('')}
        </div>
        ${renderPntaCheckbox('relationship_status_id', false)}
        ${renderVisiCheckbox('show_relationship_status', getVisiJson())}
      </div>

      <div class="onboarding-actions">
        ${backButton()}
        <button class="btn btn-primary" id="onboarding-save-btn">${BTN_SAVE_NEXT}</button>
      </div>
    </div>
  `;

  setupPntaForGroup('relationship_status_id');

  const saveBtn = document.getElementById('onboarding-save-btn') as HTMLButtonElement;
  const pnta = document.getElementById('relationship_status_id_pnta') as HTMLInputElement;

  saveBtn.addEventListener('click', async () => {
    const selected = document.querySelector('input[name="relationship_status_id"]:checked') as HTMLInputElement | null;
    if (!pnta.checked && !selected) {
      showToast(TOAST_SELECT_ANSWER, 'error');
      return;
    }
    await saveVisiFromDOM();
    // If PNTA, just advance without saving
    if (pnta.checked) {
      currentStep++;
      renderStep();
      return;
    }
    await saveAndAdvance({ relationship_status_id: parseInt(selected!.value) }, saveBtn);
  });

  attachBackHandler();
}

// ─── Step 5: Location ───────────────────────────────────────────────────────

function renderLocationStep(container: HTMLElement): void {
  const profile = store.getState().user?.profile;
  const cityIsPnta = isPnta(profile?.city);

  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">Where do you live?</h2>

      <div id="gps-status" class="onboarding-gps-spinner">
        <div class="spinner" style="width: 1.5rem; height: 1.5rem;"></div>
        <span>Detecting your location...</span>
      </div>

      <div id="location-form" style="display: none;">
        <div class="input-group">
          <label for="city" class="group-label">City</label>
          <input
            type="text"
            id="city"
            class="input"
            value="${cityIsPnta ? '' : (profile?.city || '')}"
            ${cityIsPnta ? 'disabled' : ''}
          />
        </div>

        <div class="input-group">
          <label for="state" class="group-label">State/Province</label>
          <input
            type="text"
            id="state"
            class="input"
            value="${cityIsPnta ? '' : (profile?.state || '')}"
            ${cityIsPnta ? 'disabled' : ''}
          />
        </div>

        <div class="input-group">
          <label for="country" class="group-label">Country</label>
          <input
            type="text"
            id="country"
            class="input"
            value="${cityIsPnta ? '' : (profile?.country || '')}"
            ${cityIsPnta ? 'disabled' : ''}
          />
          <label class="pnta-checkbox">
            <input type="checkbox" id="location_pnta" ${cityIsPnta ? 'checked' : ''} />
            Prefer not to answer
          </label>
          ${renderVisiCheckbox('show_location', getVisiJson())}
        </div>

        <div class="onboarding-actions">
          ${backButton()}
          <button class="btn btn-primary" id="onboarding-save-btn">${BTN_SAVE_NEXT}</button>
        </div>
      </div>
    </div>
  `;

  // Wire up location PNTA to disable/enable all three inputs
  const locationPnta = document.getElementById('location_pnta') as HTMLInputElement | null;
  if (locationPnta) {
    locationPnta.addEventListener('change', () => {
      const inputs = ['city', 'state', 'country'].map(
        id => document.getElementById(id) as HTMLInputElement
      );
      inputs.forEach(inp => {
        if (!inp) return;
        inp.disabled = locationPnta.checked;
        if (locationPnta.checked) inp.value = '';
      });
    });
  }

  attachBackHandler();
  tryGPS();
}

async function tryGPS(): Promise<void> {
  const gpsStatus = document.getElementById('gps-status')!;
  const locationForm = document.getElementById('location-form')!;

  if (!navigator.geolocation) {
    showManualLocation(gpsStatus, locationForm);
    return;
  }

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const { latitude, longitude } = position.coords;

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();

        const city = data.address?.city || data.address?.town || data.address?.village || '';
        const state = data.address?.state || '';
        const country = data.address?.country || '';

        if (city || state || country) {
          const payload: Record<string, any> = { latitude, longitude };
          if (city) payload.city = city;
          if (state) payload.state = state;
          if (country) payload.country = country;

          gpsStatus.innerHTML = `
            <div class="onboarding-gps-success">
              <i class="fas fa-check-circle"></i>
              <span>Location set to ${[city, state].filter(Boolean).join(', ')}</span>
            </div>
          `;

          try {
            await profileApi.update(payload);
            await loadUserData();

            setTimeout(() => {
              currentStep++;
              renderStep();
            }, 1000);
          } catch (err: any) {
            showApiError(err, API_ERR_SAVE_PROFILE);
            showManualLocation(gpsStatus, locationForm);
          }
        } else {
          showManualLocation(gpsStatus, locationForm);
        }
      } catch {
        showManualLocation(gpsStatus, locationForm);
      }
    },
    () => {
      showManualLocation(gpsStatus, locationForm);
    },
    { timeout: 10000, maximumAge: 60000 }
  );
}

function showManualLocation(gpsStatus: HTMLElement, locationForm: HTMLElement): void {
  gpsStatus.style.display = 'none';
  locationForm.style.display = '';

  const saveBtn = document.getElementById('onboarding-save-btn') as HTMLButtonElement;
  const locationPnta = document.getElementById('location_pnta') as HTMLInputElement;

  saveBtn?.addEventListener('click', async () => {
    const city = (document.getElementById('city') as HTMLInputElement).value.trim();
    const state = (document.getElementById('state') as HTMLInputElement).value.trim();
    const country = (document.getElementById('country') as HTMLInputElement).value.trim();

    if (!locationPnta?.checked && !city && !state && !country) {
      showToast(TOAST_SELECT_ANSWER, 'error');
      return;
    }

    await saveVisiFromDOM();

    // If PNTA, just advance
    if (locationPnta?.checked) {
      currentStep++;
      renderStep();
      return;
    }

    const payload: Record<string, any> = {};
    if (city) payload.city = city;
    if (state) payload.state = state;
    if (country) payload.country = country;

    await saveAndAdvance(payload, saveBtn);
  });

  attachBackHandler();
}

// ─── Step 6: Photos ─────────────────────────────────────────────────────────

function renderPhotoStep(container: HTMLElement): void {
  container.innerHTML = `
    <div class="onboarding-step">
      ${stepLabel()}
      <h2 class="onboarding-step-title">Add some photos</h2>

      <div id="photo-grid"></div>

      <div class="onboarding-actions" style="margin-top: var(--spacing-lg);">
        ${backButton()}
        <button class="btn btn-primary" id="onboarding-finish-btn">Finish</button>
      </div>

      <p class="onboarding-skip">
        <a href="#" id="skip-step-link">Skip for now</a>
      </p>
    </div>
  `;

  loadPhotos();

  document.getElementById('onboarding-finish-btn')?.addEventListener('click', () => {
    showToast(TOAST_PROFILE_CREATED, 'success');
    navigate('/matches');
  });

  document.getElementById('skip-step-link')?.addEventListener('click', (e) => {
    e.preventDefault();
    navigate('/matches');
  });

  attachBackHandler();
}

// ─── Shared Save Helper ─────────────────────────────────────────────────────

async function saveAndAdvance(payload: Record<string, any>, saveBtn: HTMLButtonElement): Promise<void> {
  saveBtn.disabled = true;
  const originalText = saveBtn.textContent;
  saveBtn.style.minWidth = `${saveBtn.offsetWidth}px`;
  saveBtn.innerHTML = '<div class="spinner" style="width: 1.25rem; height: 1.25rem;"></div>';

  try {
    await profileApi.update(payload);
    await loadUserData();
    currentStep++;
    renderStep();
  } catch (err: any) {
    showApiError(err, API_ERR_SAVE_PROFILE);
    saveBtn.disabled = false;
    saveBtn.textContent = originalText;
  }
}
